import { Component, inject, OnInit, signal } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
// Directive for scroll animations
import { ScrollAnimateDirective } from '../../directives/scroll-animate.directive';

// Interface for contact information structure
interface ContactInfo {
  email: string;
  phone: string;
}

// Interface for service option structure
interface ServiceOption {
  id: string;
  label: string;
  selected: boolean;
}

@Component({
  selector: 'app-contact-us',
  imports: [ReactiveFormsModule, CommonModule, ScrollAnimateDirective],
  templateUrl: './contact-us.html',
  styleUrl: './contact-us.scss',
})
export class ContactUs implements OnInit {
  // Injected services for form building, HTTP requests, and routing
  private formBuilder = inject(FormBuilder);
  private http = inject(HttpClient);
  private route = inject(ActivatedRoute);

  // Signal containing contact information for the component
  contactInfo = signal<ContactInfo>({
    email: 'info@lexwoodsolutions.com.au',
    phone: '+61 493 439 216',
  });

  // Signal containing available service options for selection
  availableServices = signal<ServiceOption[]>([
    { id: 'installation', label: 'Installation', selected: false },
    { id: 'coating', label: 'Varnishing', selected: false },
    { id: 'sanding', label: 'Sanding', selected: false },
    { id: 'oil-finishing', label: 'Polishing', selected: false },
    { id: 'colouring', label: 'Staining', selected: false },
  ]);

  // Reactive form group for contact form
  contactForm!: FormGroup;

  // Server-side validation error message
  serverValidationError: string = '';

  // Success message after form submission
  successMessage: string = '';

  // Flag to track form submission state
  isSubmitting: boolean = false;

  // Initializes the reactive form with validators
  ngOnInit() {
    this.contactForm = this.formBuilder.group({
      name: [''], // Валидация убрана
      phone: ['', [Validators.required]], // Оставлен только телефон как обязательное поле
      message: [''], // Валидация убрана
      services: [[]] // Optional field for selected services
    });

    // Clears server errors and success messages when form changes
    this.contactForm.valueChanges.subscribe(() => {
      this.serverValidationError = '';
      this.successMessage = '';
    });

    // Checks for pre-selected service from query parameters
    this.route.queryParams.subscribe(params => {
      const preselectedService = params['service'];
      if (preselectedService) {
        this.preselectService(preselectedService);
      }
    });
  }

  // Pre-selects a service based on navigation parameter
  preselectService(serviceId: string) {
    const services = this.availableServices();
    const sandingPackageServices = ['sanding', 'colouring', 'coating', 'oil-finishing'];
    let updatedServices;

    if (serviceId === 'sanding') {
      updatedServices = services.map(service => ({
        ...service,
        selected: sandingPackageServices.includes(service.id) ? true : service.selected
      }));
    } else {
      updatedServices = services.map(service => ({
        ...service,
        selected: service.id === serviceId ? true : service.selected
      }));
    }

    this.availableServices.set(updatedServices);
    this.updateServicesFormValue();
  }

  // Toggles service selection state with auto-selection logic for sanding package
  toggleService(serviceId: string) {
    const services = this.availableServices();
    const currentService = services.find(s => s.id === serviceId);
    const isNowSelected = !currentService?.selected;
    const sandingPackageServices = ['sanding', 'colouring', 'coating', 'oil-finishing'];
    let updatedServices;

    if (serviceId === 'sanding' && isNowSelected) {
      updatedServices = services.map(service => ({
        ...service,
        selected: sandingPackageServices.includes(service.id) ? true : service.selected
      }));
    } else if (serviceId === 'sanding' && !isNowSelected) {
      updatedServices = services.map(service => ({
        ...service,
        selected: sandingPackageServices.includes(service.id) ? false : service.selected
      }));
    } else {
      updatedServices = services.map(service => ({
        ...service,
        selected: service.id === serviceId ? !service.selected : service.selected
      }));
    }

    this.availableServices.set(updatedServices);
    this.updateServicesFormValue();
  }

  // Updates form control value based on selected services
  updateServicesFormValue() {
    const selectedServiceIds = this.availableServices()
      .filter(service => service.selected)
      .map(service => service.id);
    this.contactForm.patchValue({ services: selectedServiceIds });
  }

  // Resets all service selections to unselected state
  resetServices() {
    const services = this.availableServices();
    const resetServices = services.map(service => ({
      ...service,
      selected: false
    }));
    this.availableServices.set(resetServices);
    this.updateServicesFormValue();
  }

  // Getter for easy access to form controls
  get f(): { [key: string]: AbstractControl } {
    return this.contactForm.controls;
  }

  // Заглушка, так как поле не обязательное
  getNameErrorMessage(): string {
    return '';
  }

  // Проверка только для телефона
  getPhoneErrorMessage(): string {
    if (this.f['phone'].hasError('required')) {
      return 'Phone number is required';
    }
    return '';
  }

  // Заглушка, так как поле не обязательное
  getMessageErrorMessage(): string {
    return '';
  }

  // Handles form submission with validation and HTTP request
  onSubmit() {
    // Removes focus from all fields
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    // Validates form and marks all fields as touched if invalid
    if (this.contactForm.invalid) {
      Object.keys(this.contactForm.controls).map(key =>
        this.contactForm.get(key)?.markAsTouched()
      );
      return;
    }

    this.isSubmitting = true;
    this.serverValidationError = '';
    this.successMessage = '';

    // Prepares contact data for submission including selected services
    const selectedServices = this.availableServices()
      .filter(service => service.selected)
      .map(service => service.label);

    const contactData = {
      name: this.contactForm.value.name,
      phone: this.contactForm.value.phone, // Используем телефон вместо email
      message: this.contactForm.value.message,
      service: selectedServices.length > 0 ? selectedServices.join(', ') : undefined
    };

    // Sends HTTP POST request to backend API
    this.http.post('https://floorsydney-email-api.volodymyrfloor.workers.dev', contactData).subscribe({
      next: (response: any) => {

        // Отправляем успешную конверсию в GTM
        if (typeof window !== 'undefined') {
          (window as any).dataLayer = (window as any).dataLayer || [];
          (window as any).dataLayer.push({
            event: 'contact_form_success', // Убедитесь, что это имя совпадает с триггером в GTM!
            formType: 'contact_us'
          });

          // ВРЕМЕННЫЙ ЛОГ ДЛЯ ОТЛАДКИ :
          // console.log('⚡ КОД ОТРАБОТАЛ! Событие ушло.');
          // console.log('📦 Массив dataLayer сейчас выглядит так:', (window as any).dataLayer);
        }
        // --------------------------------

        setTimeout(() => {
          this.isSubmitting = false;
          this.contactForm.reset();
          this.resetServices();
          this.successMessage = 'Thank you for your message! We will get back to you soon.';

          setTimeout(() => {
            this.successMessage = '';
          }, 5000);
        }, 500);
      },
      error: (error) => {
        this.isSubmitting = false;
        this.serverValidationError = error.error?.message || 'An error occurred. Please try again later.';
      }
    });
  }
}